import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { publicLegalApi } from '../services/api';
import CookieBanner from '../components/CookieBanner';
import './LegalPage.css';

interface LegalPageProps {
  type: 'impressum' | 'datenschutz' | 'agb' | 'avv';
}

const TITLES: Record<string, string> = {
  impressum: 'Impressum',
  datenschutz: 'Datenschutzerklärung',
  agb: 'Allgemeine Geschäftsbedingungen',
  avv: 'Auftragsverarbeitungsvertrag',
};

/* ================================================================
   AGB – Fallback plain text (shown when no DB text exists)
   ================================================================ */
const AGB_FALLBACK = `Allgemeine Geschäftsbedingungen (AGB) für CoachDesk365

1. Geltungsbereich
Diese AGB gelten für die Nutzung der Plattform CoachDesk365 durch registrierte Coaches.

2. Vertragsgegenstand
CoachDesk365 stellt eine webbasierte Software zur Verwaltung von Coaching-Praxen bereit (Klientenverwaltung, Terminplanung, Rechnungsstellung, Dokumentenmanagement).

3. Registrierung und Konto
- Die Registrierung ist nur für geschäftsfähige Personen zulässig.
- Der Nutzer ist für die Sicherheit seiner Zugangsdaten verantwortlich.
- Jeder Nutzer darf nur ein Konto erstellen.

4. Datenschutz und Vertraulichkeit
- Der Nutzer ist als Verantwortlicher im Sinne der DSGVO für die von ihm eingegebenen Klientendaten verantwortlich.
- CoachDesk365 verarbeitet diese Daten im Auftrag (Auftragsverarbeitung gemäß Art. 28 DSGVO).
- Ein Auftragsverarbeitungsvertrag (AVV) kann in den Einstellungen unterzeichnet werden.

5. Verfügbarkeit
Wir bemühen uns um eine hohe Verfügbarkeit der Plattform, garantieren jedoch keine ununterbrochene Erreichbarkeit.

6. Kündigung
- Das Konto kann jederzeit in den Einstellungen gelöscht werden.
- Bei Löschung werden alle Daten DSGVO-konform entfernt (Rechnungen werden anonymisiert aufbewahrt).

7. Haftung
Die Haftung ist auf Vorsatz und grobe Fahrlässigkeit beschränkt. Eine Haftung für die Richtigkeit der vom Nutzer eingegebenen Daten ist ausgeschlossen.

8. Änderungen der AGB
Änderungen werden per E-Mail angekündigt. Die weitere Nutzung nach Ankündigung gilt als Zustimmung.

9. Anwendbares Recht
Es gilt das Recht der Bundesrepublik Deutschland.

Stand: Februar 2026`;

/* ================================================================
   Impressum – Structured card layout
   ================================================================ */
function ImpressumPage() {
  return (
    <>
      <div className="acl-impressum">
        <div className="acl-grid">
          <div className="acl-card">
            <h3>Angaben gemäß § 5 TMG</h3>
            <p><strong>Sara Wilhelm</strong></p>
            <p>CoachDesk365</p>
            <p>Postfach 101009</p>
            <p>40839 Ratingen</p>
          </div>

          <div className="acl-card">
            <h3>Kontakt</h3>
            <p>Telefon: <a href="tel:+491702087798">0170 208 77 98</a></p>
            <p>E-Mail: <a href="mailto:service@coachdesk365.com">service@coachdesk365.com</a></p>
            <p>Web: <a href="https://www.coachdesk365.com" target="_blank" rel="noreferrer">www.coachdesk365.com</a></p>
          </div>

          <div className="acl-card">
            <h3>Plattform</h3>
            <p>CoachDesk365 ist eine webbasierte SaaS-Plattform für die Verwaltung von Coaching-Praxen.</p>
            <p>Funktionsumfang: Klientenverwaltung, Terminplanung, Rechnungsstellung, Dokumentenmanagement, Klienten-Portal, Buchungssystem, Methoden-Bibliothek und Qualitätsmanagement.</p>
          </div>

          <div className="acl-card">
            <h3>Hosting</h3>
            <p><strong>Hetzner Online GmbH</strong></p>
            <p>Industriestraße 25</p>
            <p>91710 Gunzenhausen, Deutschland</p>
            <p className="acl-hint">Server-Standort: Deutschland</p>
          </div>

          <div className="acl-card">
            <h3>Redaktionell verantwortlich</h3>
            <p><strong>Sara Wilhelm</strong></p>
            <p>Postfach 101009</p>
            <p>40839 Ratingen</p>
          </div>

          <div className="acl-card">
            <h3>EU-Streitschlichtung</h3>
            <p>Online-Streitbeilegung:<br />
              <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noreferrer">ec.europa.eu/consumers/odr</a>
            </p>
            <p className="acl-hint">
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </div>
        </div>

        <hr className="acl-divider" />
      </div>

      <div className="acl-legal">
        <div className="acl-legal-grid">
          <div className="acl-legal-block">
            <h3>Haftung für Inhalte</h3>
            <p>Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden werden wir diese Inhalte umgehend entfernen.</p>
          </div>
          <div className="acl-legal-block">
            <h3>Haftung für Links</h3>
            <p>Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.</p>
          </div>
        </div>

        <div className="acl-legal-grid">
          <div className="acl-legal-block">
            <h3>Urheberrecht</h3>
            <p>Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.</p>
          </div>
          <div className="acl-legal-block">
            <h3>Hinweis</h3>
            <p>Dieses Impressum gilt auch für unsere Social-Media-Auftritte auf LinkedIn, Instagram und weiteren Plattformen.</p>
            <p className="acl-sub-hint" style={{ textAlign: 'left' }}>Angaben gemäß § 5 TMG, § 55 RStV</p>
          </div>
        </div>
      </div>
    </>
  );
}

/* ================================================================
   Datenschutz – Structured card layout
   ================================================================ */
function DatenschutzPage() {
  return (
    <div className="acl-dse">
      <div className="acl-dse-intro">
        <h3>Datenschutz auf einen Blick</h3>
        <p>Die folgenden Hinweise geben einen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website bzw. die Plattform CoachDesk365 nutzen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.</p>
      </div>

      {/* Verantwortliche & Datenerfassung */}
      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Verantwortliche Stelle</h3>
          <p><strong>Sara Wilhelm</strong></p>
          <p>CoachDesk365</p>
          <p>Postfach 101009</p>
          <p>40839 Ratingen</p>
          <p>E-Mail: <a href="mailto:service@coachdesk365.com">service@coachdesk365.com</a></p>
        </div>

        <div className="acl-dse-card">
          <h3>Wie erfassen wir Ihre Daten?</h3>
          <h4>Von Ihnen mitgeteilt</h4>
          <p>Daten, die Sie bei der Registrierung, im Kontaktformular, über die öffentliche Buchungsseite oder per E-Mail übermitteln.</p>
          <h4>Im Rahmen der Plattformnutzung</h4>
          <p>Klientenstammdaten, Sitzungsnotizen, Rechnungsdaten, Buchungen, Dokumente und Support-Anfragen, die Sie als Coach in der Plattform anlegen.</p>
          <h4>Automatisch erfasst</h4>
          <p>Technische Daten (Browser, Betriebssystem, Uhrzeit des Seitenaufrufs) werden beim Besuch automatisch durch unsere IT-Systeme erfasst.</p>
        </div>
      </div>

      {/* Ihre Rechte */}
      <div className="acl-dse-grid">
        <div className="acl-dse-card acl-dse-full">
          <h3>Ihre Rechte</h3>
          <div className="rights-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <h4>Auskunft &amp; Löschung</h4>
              <p>Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten Daten sowie ein Recht auf Berichtigung oder Löschung.</p>
            </div>
            <div>
              <h4>Widerruf</h4>
              <p>Erteilte Einwilligungen zur Datenverarbeitung können Sie jederzeit für die Zukunft widerrufen.</p>
            </div>
            <div>
              <h4>Beschwerde</h4>
              <p>Ihnen steht ein Beschwerderecht bei der zuständigen Datenschutz-Aufsichtsbehörde zu.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Hosting & Cookies ===== */}
      <hr className="acl-dse-divider" />
      <p className="acl-dse-section">Hosting &amp; Lokale Speicherung</p>

      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Hosting</h3>
          <p>Wir hosten unsere Website und die Plattform bei:</p>
          <p><strong>Hetzner Online GmbH</strong></p>
          <p>Industriestraße 25, 91710 Gunzenhausen, Deutschland</p>
          <p style={{ marginTop: 8 }}>Server-Standort: Deutschland. Ihre Daten verlassen nicht die EU.</p>
          <p style={{ marginTop: 8 }}>Datenschutzerklärung des Hosters:<br />
            <a href="https://www.hetzner.com/de/legal/privacy-policy" target="_blank" rel="noreferrer">www.hetzner.com/de/legal/privacy-policy</a>
          </p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an zuverlässiger Bereitstellung).</p>
        </div>

        <div className="acl-dse-card">
          <h3>Lokale Speicherung (localStorage)</h3>
          <p>CoachDesk365 verwendet <strong>keine Tracking- oder Marketing-Cookies</strong>. Stattdessen nutzen wir den lokalen Speicher Ihres Browsers (localStorage) für:</p>
          <h4>JWT-Token</h4>
          <p>Authentifizierungs-Token für Ihre aktive Sitzung. Wird bei Abmeldung gelöscht.</p>
          <h4>Theme-Einstellung</h4>
          <p>Ihre gewählte Farbgebung der Benutzeroberfläche.</p>
          <h4>Cookie-Consent</h4>
          <p>Speicherung Ihrer Zustimmung zum Cookie-Banner.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (technisch notwendig für den Betrieb der Plattform). Es werden keine Daten an Dritte übermittelt.</p>
        </div>
      </div>

      {/* ===== Konto & Authentifizierung ===== */}
      <hr className="acl-dse-divider" />
      <p className="acl-dse-section">Konto &amp; Authentifizierung</p>

      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Registrierung &amp; Login</h3>
          <p>Bei der Registrierung erfassen wir: Name, E-Mail-Adresse und ein selbst gewähltes Passwort.</p>
          <h4>Passwortsicherheit</h4>
          <p>Passwörter werden mit <strong>bcrypt</strong> gehasht gespeichert. Das Klartext-Passwort wird zu keinem Zeitpunkt gespeichert.</p>
          <h4>JWT-Authentifizierung</h4>
          <p>Nach dem Login erhalten Sie ein zeitlich begrenztes JSON Web Token (JWT), das für die Dauer der Sitzung gültig ist.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).</p>
        </div>

        <div className="acl-dse-card">
          <h3>Zwei-Faktor-Authentifizierung (2FA)</h3>
          <p>Zur zusätzlichen Absicherung Ihres Kontos bieten wir 2FA an:</p>
          <h4>TOTP (Authenticator-App)</h4>
          <p>Zeitbasierte Einmalpasswörter über eine Authenticator-App Ihrer Wahl.</p>
          <h4>E-Mail-Code</h4>
          <p>Ein einmaliger Code wird an Ihre registrierte E-Mail-Adresse gesendet.</p>
          <h4>Backup-Codes</h4>
          <p>Einmalig verwendbare Codes für den Notfallzugang, falls Ihre 2FA-Methode nicht verfügbar ist.</p>
          <p className="acl-hint">Die 2FA-Daten (TOTP-Secret, Backup-Codes) werden verschlüsselt in der Datenbank gespeichert.</p>
        </div>
      </div>

      {/* ===== Datenerfassung ===== */}
      <hr className="acl-dse-divider" />
      <p className="acl-dse-section">Datenerfassung auf dieser Plattform</p>

      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Kontaktformular &amp; E-Mail</h3>
          <p>Wenn Sie uns per Kontaktformular oder E-Mail kontaktieren, werden Ihre Angaben (Name, E-Mail, Nachricht) zwecks Bearbeitung der Anfrage bei uns gespeichert.</p>
          <p>Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) bzw. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).</p>
          <p className="acl-hint">Die Daten verbleiben bei uns, bis Sie die Löschung verlangen oder der Zweck entfällt.</p>
        </div>

        <div className="acl-dse-card">
          <h3>Öffentliche Buchungsseite</h3>
          <p>Über die öffentliche Buchungsseite können Interessenten ohne Konto einen Termin anfragen. Dabei werden erfasst:</p>
          <p><strong>Name, E-Mail-Adresse und Nachricht.</strong></p>
          <p>Diese Daten werden ausschließlich zur Bearbeitung der Buchungsanfrage verwendet und dem jeweiligen Coach zugeordnet.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen).</p>
        </div>
      </div>

      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Klienten-Portal</h3>
          <p>Klienten erhalten auf Einladung des Coaches Zugang zum Portal. Im Portal können Klienten:</p>
          <p>Ihr Profil einsehen, Änderungsanträge stellen, Support-Tickets erstellen und ihre DSGVO-Rechte (Auskunft, Löschung) wahrnehmen.</p>
          <p className="acl-hint">Der Zugang erfolgt über einen individuellen Einladungslink mit separatem Login. Sitzungsnotizen des Coaches sind im Portal nicht sichtbar.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).</p>
        </div>

        <div className="acl-dse-card">
          <h3>E-Mail-Versand</h3>
          <p>CoachDesk365 versendet automatisierte E-Mails in folgenden Fällen:</p>
          <p>Rechnungsversand, Angebotsversand, Termin-Erinnerungen (24 h vorher), Mahnungen (4-Stufen-Verfahren), Portal-Einladungen, Support-Benachrichtigungen und 2FA-Codes.</p>
          <p>Der Versand erfolgt über SMTP. E-Mail-Inhalte werden nicht an Dritte weitergegeben.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) bzw. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).</p>
        </div>
      </div>

      {/* ===== Drittanbieter ===== */}
      <hr className="acl-dse-divider" />
      <p className="acl-dse-section">Drittanbieter &amp; Integrationen</p>

      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Google Drive (optional)</h3>
          <p>Coaches können optional Google Drive anbinden, um Dokumente (Rechnungen, Angebote, Verträge, Notizen) automatisch in ihrem eigenen Drive-Konto zu sichern.</p>
          <p>Die Verbindung erfolgt über OAuth 2.0. CoachDesk365 erhält dabei nur Zugriff auf einen eigenen App-Ordner in Ihrem Drive.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Die Integration kann jederzeit in den Einstellungen deaktiviert werden.</p>
        </div>

        <div className="acl-dse-card">
          <h3>Zoom (optional)</h3>
          <p>Coaches können optional Zoom integrieren, um automatisch Meeting-Links für Buchungen zu erstellen.</p>
          <p>Die Verbindung erfolgt über OAuth 2.0. Es werden dabei keine Klientendaten an Zoom übertragen – lediglich Meeting-Termine werden erstellt.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Die Integration kann jederzeit in den Einstellungen deaktiviert werden.</p>
        </div>
      </div>

      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Stripe (optional)</h3>
          <p>Für die Zahlungsabwicklung von Abonnements kann Stripe eingesetzt werden. Zahlungsdaten (Kreditkarte, SEPA) werden ausschließlich von Stripe verarbeitet und gespeichert.</p>
          <p>CoachDesk365 speichert keine Zahlungsdaten – lediglich die Stripe-Kunden-ID und den Abonnement-Status.</p>
          <p style={{ marginTop: 8 }}>Datenschutzerklärung von Stripe:<br />
            <a href="https://stripe.com/de/privacy" target="_blank" rel="noreferrer">stripe.com/de/privacy</a>
          </p>
          <p className="acl-hint">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).</p>
        </div>

        <div className="acl-dse-card">
          <h3>Google Fonts (lokal)</h3>
          <p>Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten lokal installierte Google Fonts.</p>
          <p><strong>Eine Verbindung zu Servern von Google findet dabei nicht statt.</strong></p>
          <p className="acl-hint">Es werden keine Daten an Google übertragen.</p>
        </div>
      </div>

      {/* ===== Datensicherheit ===== */}
      <hr className="acl-dse-divider" />
      <p className="acl-dse-section">Datensicherheit</p>

      <div className="acl-dse-grid">
        <div className="acl-dse-card acl-dse-full">
          <h3>Technische Sicherheitsmaßnahmen</h3>
          <div className="rights-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <h4>Verschlüsselung</h4>
              <p>Alle Daten werden per SSL/TLS verschlüsselt übertragen (HTTPS). Passwörter sind mit bcrypt gehasht, 2FA-Secrets und Backup-Codes verschlüsselt gespeichert.</p>
            </div>
            <div>
              <h4>Backups &amp; Audit-Log</h4>
              <p>Regelmäßige Datenbank-Backups sichern Ihre Daten. Alle sicherheitsrelevanten Aktionen (Login, Datenänderungen, Löschungen) werden im Audit-Log protokolliert.</p>
            </div>
            <div>
              <h4>Zugriffskontrolle</h4>
              <p>Rollenbasierte Zugriffskontrolle stellt sicher, dass Coaches nur ihre eigenen Daten sehen. Klienten im Portal haben keinen Zugriff auf Coach-Notizen.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Auftragsverarbeitung ===== */}
      <hr className="acl-dse-divider" />
      <p className="acl-dse-section">Auftragsverarbeitung &amp; Speicherdauer</p>

      <div className="acl-dse-grid">
        <div className="acl-dse-card">
          <h3>Auftragsverarbeitung (Art. 28 DSGVO)</h3>
          <p>Wenn Sie als Coach Klientendaten in CoachDesk365 verwalten, sind <strong>Sie der Verantwortliche</strong> im Sinne der DSGVO für diese Daten.</p>
          <p><strong>CoachDesk365 (Sara Wilhelm)</strong> handelt als Auftragsverarbeiter und verarbeitet die Daten ausschließlich nach Ihrer Weisung.</p>
          <p>Ein Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO kann in den Plattform-Einstellungen abgeschlossen werden.</p>
          <p className="acl-hint">Rechtsgrundlage: Art. 28 DSGVO.</p>
        </div>

        <div className="acl-dse-card">
          <h3>Speicherdauer</h3>
          <h4>Konto- und Profildaten</h4>
          <p>Bis zur Löschung des Kontos durch den Nutzer.</p>
          <h4>Klientendaten &amp; Sitzungsnotizen</h4>
          <p>Bis zur Löschung durch den Coach oder bis zum Löschersuchen des Klienten.</p>
          <h4>Rechnungen</h4>
          <p>10 Jahre (gesetzliche Aufbewahrungspflicht nach HGB/AO). Nach Ablauf werden Rechnungen anonymisiert.</p>
          <h4>Audit-Logs</h4>
          <p>12 Monate, danach automatische Löschung.</p>
          <h4>Buchungsanfragen</h4>
          <p>Bis zur Bearbeitung bzw. maximal 6 Monate nach Anfrage.</p>
        </div>
      </div>

      {/* ===== Datenweitergabe ===== */}
      <div className="acl-dse-grid">
        <div className="acl-dse-card acl-dse-full">
          <h3>Datenweitergabe in Drittstaaten</h3>
          <p>Ihre Daten werden auf Servern in Deutschland (Hetzner) gespeichert und verlassen grundsätzlich nicht die EU. Eine Übermittlung in Drittstaaten findet nur statt, wenn Sie optional Drittanbieter-Integrationen aktivieren (Google Drive, Zoom, Stripe). In diesem Fall gelten die EU-Standardvertragsklauseln der jeweiligen Anbieter.</p>
        </div>
      </div>

      <p className="acl-sub-hint" style={{ paddingBottom: 40 }}>
        Stand: März 2026.
      </p>
    </div>
  );
}

/* ================================================================
   AGB – Plain text in white card
   ================================================================ */
function AgbPage({ content }: { content: string }) {
  return (
    <div className="acl-agb">
      <div className="acl-agb-card">
        <h1>Allgemeine Geschäftsbedingungen</h1>
        <div className="content">{content}</div>
      </div>
    </div>
  );
}

/* ================================================================
   AVV – Auftragsverarbeitungsvertrag
   ================================================================ */
function AvvPage() {
  return (
    <div className="acl-agb">
      <div className="acl-agb-card">
        <h1>Auftragsverarbeitungsvertrag (AVV)</h1>
        <div className="content">
          <p>Gemäß Art. 28 DSGVO schließen wir mit jedem Kunden, der personenbezogene Daten über CoachDesk365 verarbeitet, einen Auftragsverarbeitungsvertrag (AVV) ab.</p>
          <br />
          <p><strong>Vertragsgegenstand:</strong> Der Auftragnehmer (CoachDesk365 / Sara Wilhelm) verarbeitet personenbezogene Daten im Auftrag des Auftraggebers (Coach) ausschließlich zum Zweck der Bereitstellung der CoachDesk365-Plattform.</p>
          <br />
          <p><strong>Art der Daten:</strong> Kontaktdaten von Klienten (Name, E-Mail, Adresse, Telefon), Coaching-Sitzungsdaten, Rechnungsdaten, Terminbuchungen.</p>
          <br />
          <p><strong>Betroffene Personen:</strong> Klienten des Auftraggebers, die über die Plattform verwaltet werden.</p>
          <br />
          <p><strong>Technische und organisatorische Maßnahmen (TOM):</strong></p>
          <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
            <li>Server-Standort: Deutschland (Hetzner Cloud, Nürnberg)</li>
            <li>SSL/TLS-Verschlüsselung für alle Datenübertragungen</li>
            <li>Zugriffskontrolle mit rollenbasierter Autorisierung</li>
            <li>Regelmäßige Backups mit verschlüsselter Speicherung</li>
            <li>Audit-Log aller Datenoperationen</li>
            <li>DSGVO-konformes Löschkonzept mit Soft-Delete</li>
          </ul>
          <br />
          <p><strong>Kontakt für AVV-Anfragen:</strong></p>
          <p>Sara Wilhelm · <a href="mailto:info@coachdesk365.com">info@coachdesk365.com</a></p>
          <br />
          <p style={{ fontSize: 13, color: '#6b7280' }}>Der vollständige AVV kann nach Registrierung in den Kontoeinstellungen eingesehen und digital unterzeichnet werden.</p>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   Legal Footer
   ================================================================ */
function LegalFooter() {
  return (
    <div className="legal-footer">
      <a href="/impressum">Impressum</a>
      <a href="/datenschutz">Datenschutz</a>
      <a href="/avv">AVV</a>
      <a href="/agb">AGB</a>
    </div>
  );
}

/* ================================================================
   Main LegalPage component
   ================================================================ */
export default function LegalPage({ type }: LegalPageProps) {
  const { t, i18n } = useTranslation();
  const [agbContent, setAgbContent] = useState('');
  const [loading, setLoading] = useState(type === 'agb');

  useEffect(() => {
    if (type !== 'agb') return;
    setLoading(true);
    publicLegalApi
      .getText(type)
      .then((res) => setAgbContent(res.data.content))
      .catch(() => setAgbContent(AGB_FALLBACK))
      .finally(() => setLoading(false));
  }, [type]);

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', background: 'var(--bg-card)', color: 'var(--text-muted)',
      }}>
        Wird geladen...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-card)' }}>
      {/* Hero Header */}
      <div className="legal-hero">
        <h1>{TITLES[type]}</h1>
      </div>
      <div style={{ height: 40 }} />

      {/* German-only notice for EN users */}
      {i18n.language === 'en' && t('legal.germanOnlyNotice') && (
        <div className="legal-lang-notice">{t('legal.germanOnlyNotice')}</div>
      )}

      {/* Content */}
      {type === 'impressum' && <ImpressumPage />}
      {type === 'datenschutz' && <DatenschutzPage />}
      {type === 'agb' && <AgbPage content={agbContent} />}
      {type === 'avv' && <AvvPage />}

      <LegalFooter />
      <CookieBanner />
    </div>
  );
}
